import { Stack, Box, Collapse } from '@mui/material';
import { Typography, colors, IconButton, Tooltip } from '@sweep-io/sweep-design';
import { Plus, ChevronDown, ChevronRight } from '@sweep-io/sweep-design/dist/icons';
import { PromptGroup } from '@server/prompts';
import { CategoryListItem } from './CategoryListItem';
import { AllPromptsCategoryItem } from './AllPromptsCategoryItem';
import { CategoryForm } from './CategoryForm';
import { useState, useRef, useEffect } from 'react';
import ConfirmDestructiveDialog from '../../../common/dialogs/ConfirmDestructiveDialog';
import { CategoryData, CategoryFormState } from '../types';
import { CategoryMenuAction } from './utils';

interface PromptsLibraryLeftPanelProps {
  categories: PromptGroup[];
  selectedCategoryId: string | null;
  onCategorySelect: (categoryId: string | null) => void;
  onCreateCategory: (data: CategoryData) => Promise<void>;
  onEditCategory: (categoryId: string, data: CategoryData) => Promise<void>;
  onDeleteCategory: (categoryId: string) => Promise<void>;
  onPinCategory: (categoryId: string) => Promise<void>;
  onUnpinCategory: (categoryId: string) => Promise<void>;
  totalPromptsCount: number;
  categoryFormState: CategoryFormState;
  onCategoryFormStateChange: (nextState: CategoryFormState) => void;
}

export const PromptsLibraryLeftPanel = ({
  categories,
  selectedCategoryId,
  onCategorySelect,
  onCreateCategory,
  onEditCategory,
  onDeleteCategory,
  onPinCategory,
  onUnpinCategory,
  totalPromptsCount,
  categoryFormState,
  onCategoryFormStateChange,
}: PromptsLibraryLeftPanelProps) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [draftCategoryName, setDraftCategoryName] = useState('');
  const [draftCategoryDescription, setDraftCategoryDescription] = useState('');
  const [categoryToDelete, setCategoryToDelete] = useState<PromptGroup | null>(null);
  const categoryRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const scrollToCategory = useRef<string | null>(null);

  const isFormOpen = categoryFormState.mode !== null;
  const isEditMode = categoryFormState.mode === 'edit';

  const resetCategoryDraft = () => {
    setDraftCategoryName('');
    setDraftCategoryDescription('');
  };

  useEffect(() => {
    if (scrollToCategory.current === 'pending' && selectedCategoryId) {
      setTimeout(() => {
        const element = categoryRefs.current[selectedCategoryId];
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          scrollToCategory.current = null;
        }
      }, 100);
    }
  }, [selectedCategoryId]);

  const handleFormSubmit = () => {
    if (!draftCategoryName.trim()) return;

    const data: CategoryData = {
      name: draftCategoryName.trim(),
      description: draftCategoryDescription.trim(),
    };

    if (isEditMode && categoryFormState.editingCategoryId) {
      onEditCategory(categoryFormState.editingCategoryId, data);
    } else {
      onCreateCategory(data);
      scrollToCategory.current = 'pending';
    }
    onCategoryFormStateChange({ mode: null, editingCategoryId: null });
    resetCategoryDraft();
  };

  const handleFormCancel = () => {
    onCategoryFormStateChange({ mode: null, editingCategoryId: null });
    resetCategoryDraft();
  };

  const startEditing = (category: PromptGroup) => {
    onCategoryFormStateChange({ mode: 'edit', editingCategoryId: category.id });
    setDraftCategoryName(category.name);
    setDraftCategoryDescription(category.description || '');
  };

  const handleCategoryAction = (categoryId: string, action: CategoryMenuAction) => {
    const category = categories.find((c) => c.id === categoryId);
    if (!category) return;

    switch (action) {
      case 'pin':
        if (category.isPinned) {
          onUnpinCategory(categoryId);
        } else {
          onPinCategory(categoryId);
        }
        break;
      case 'edit':
        startEditing(category);
        break;
      case 'delete':
        setCategoryToDelete(category);
        break;
    }
  };

  const handleConfirmDelete = () => {
    if (categoryToDelete) {
      onDeleteCategory(categoryToDelete.id);
      setCategoryToDelete(null);
    }
  };

  return (
    <>
      <ConfirmDestructiveDialog
        open={!!categoryToDelete}
        onClose={() => setCategoryToDelete(null)}
        onConfirm={handleConfirmDelete}
        title="Are you sure?"
        confirmText="Delete"
        cancelText="Cancel"
      >
        Deleting {categoryToDelete?.name} will delete all the prompts it contains.
      </ConfirmDestructiveDialog>

      <Box
        sx={{
          width: '338px',
          minWidth: '338px',
          height: '100%',
          borderRight: `1px solid ${colors.grey[300]}`,
          backgroundColor: colors.white,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ padding: '16px' }}>
          <Typography variant="h4-medium">Prompt library</Typography>
        </Box>

        <Box
          sx={{
            padding: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            flex: 1,
            overflow: 'hidden',
            height: '100%',
          }}
        >
          <Box
            sx={{
              pointerEvents: isFormOpen ? 'none' : 'auto',
              opacity: isFormOpen ? 0.5 : 1,
              transition: 'opacity 0.2s',
            }}
          >
            <AllPromptsCategoryItem
              totalCount={totalPromptsCount}
              isSelected={selectedCategoryId === null}
              onClick={() => onCategorySelect(null)}
            />
          </Box>

          <Stack gap={1} flex={1} minHeight={0} overflow="hidden">
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                cursor: isFormOpen ? 'default' : 'pointer',
                pointerEvents: isFormOpen ? 'none' : 'auto',
                opacity: isFormOpen ? 0.5 : 1,
                transition: 'opacity 0.2s',
              }}
              onClick={() => setIsExpanded(!isExpanded)}
            >
              <Stack direction="row" gap={0.5} alignItems="center">
                {isExpanded ? <ChevronDown size="small" /> : <ChevronRight size="small" />}
                <Typography variant="caption-bold" color={colors.grey[800]}>
                  Categories ({categories.length})
                </Typography>
              </Stack>

              <Stack
                direction="row"
                gap={0.5}
                pr={1}
                alignItems="center"
                sx={{ svg: { path: { stroke: colors.blue[500] } } }}
              >
                {!isFormOpen && (
                  <Tooltip title="Add Category">
                    <IconButton
                      size="tiny"
                      variant="flat"
                      onClick={(e) => {
                        e.stopPropagation();
                        onCategoryFormStateChange({ mode: 'create', editingCategoryId: null });
                      }}
                    >
                      <Plus />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </Stack>

            {isExpanded && (
              <Stack gap={1} flex={1} minHeight={0} overflow="hidden">
                <Collapse in={isFormOpen}>
                  <CategoryForm
                    name={draftCategoryName}
                    onNameChange={setDraftCategoryName}
                    onSubmit={handleFormSubmit}
                    onCancel={handleFormCancel}
                    description={draftCategoryDescription}
                    onDescriptionChange={setDraftCategoryDescription}
                    shouldFocus={isFormOpen}
                  />
                </Collapse>

                <Box
                  sx={{
                    pointerEvents: isFormOpen ? 'none' : 'auto',
                    opacity: isFormOpen ? 0.5 : 1,
                    transition: 'opacity 0.2s',
                    flex: 1,
                    minHeight: 0,
                    overflow: 'auto',
                    paddingBottom: 2,
                  }}
                >
                  <Stack gap={1}>
                    {categories.map((category) => (
                      <Box
                        key={category.id}
                        ref={(el: HTMLDivElement | null) => {
                          categoryRefs.current[category.id] = el;
                        }}
                      >
                        <CategoryListItem
                          category={category}
                          isSelected={selectedCategoryId === category.id}
                          onClick={() => onCategorySelect(category.id)}
                          onMenuAction={(action) => handleCategoryAction(category.id, action)}
                        />
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </Stack>
            )}
          </Stack>
        </Box>
      </Box>
    </>
  );
};
